import { FC, FormEvent, useCallback, useEffect, useState } from 'react';
import { PencilIcon } from '@heroicons/react/24/outline';
import { Layout } from '@components';
import { useAuth } from '@data/contexts';
import { getProfile, updateProfile } from '@services/admin.service';

export const Profile: FC = () => {
    const { admin, updateAdmin } = useAuth();
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editing, setEditing] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setLoading(true);
        getProfile()
            .then((res) => {
                setFirstName(res.data.firstName);
                setLastName(res.data.lastName);
                updateAdmin(res.data);
            })
            .catch((err) => setError(err.message))
            .finally(() => setLoading(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSubmit = useCallback(async (e: FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setMessage(null);
        setError(null);

        try {
            const res = await updateProfile({ firstName, lastName });
            updateAdmin(res.data);
            setMessage('Profil mis à jour avec succès');
            setEditing(false);
        } catch (err: any) {
            setError(err.message || 'Erreur lors de la mise à jour');
        } finally {
            setSaving(false);
        }
    }, [firstName, lastName, updateAdmin]);

    const handleCancel = useCallback(() => {
        setFirstName(admin?.firstName || '');
        setLastName(admin?.lastName || '');
        setEditing(false);
        setMessage(null);
        setError(null);
    }, [admin]);

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <span className="loading loading-spinner loading-lg"></span>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="p-6 max-w-2xl">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold">Mon profil</h1>
                    {!editing && (
                        <button
                            onClick={() => setEditing(true)}
                            className="btn btn-ghost btn-sm gap-2"
                        >
                            <PencilIcon className="w-4 h-4" />
                            Modifier
                        </button>
                    )}
                </div>

                {message && (
                    <div className="alert alert-success mb-4">
                        <span>{message}</span>
                    </div>
                )}

                {error && (
                    <div className="alert alert-error mb-4">
                        <span>{error}</span>
                    </div>
                )}

                {!editing ? (
                    <div className="space-y-4">
                        <div>
                            <p className="text-sm text-base-content/50 mb-1">Adresse email</p>
                            <p className="font-medium">{admin?.email || '-'}</p>
                        </div>
                        <div>
                            <p className="text-sm text-base-content/50 mb-1">Prénom</p>
                            <p className="font-medium">{admin?.firstName || '-'}</p>
                        </div>
                        <div>
                            <p className="text-sm text-base-content/50 mb-1">Nom</p>
                            <p className="font-medium">{admin?.lastName || '-'}</p>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <div className="form-control w-full mb-4">
                            <label className="label">
                                <span className="label-text">Adresse email</span>
                            </label>
                            <input
                                type="email"
                                className="input input-bordered w-full bg-base-200"
                                value={admin?.email || ''}
                                disabled
                            />
                        </div>

                        <div className="form-control w-full mb-4">
                            <label className="label">
                                <span className="label-text">Prénom</span>
                            </label>
                            <input
                                type="text"
                                placeholder="Votre prénom"
                                className="input input-bordered w-full"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                disabled={saving}
                            />
                        </div>

                        <div className="form-control w-full mb-4">
                            <label className="label">
                                <span className="label-text">Nom</span>
                            </label>
                            <input
                                type="text"
                                placeholder="Votre nom"
                                className="input input-bordered w-full"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                disabled={saving}
                            />
                        </div>

                        <div className="flex gap-3">
                            <button
                                type="submit"
                                className={`btn btn-primary ${saving ? 'loading' : ''}`}
                                disabled={saving}
                            >
                                {saving ? 'Enregistrement...' : 'Enregistrer'}
                            </button>
                            <button
                                type="button"
                                className="btn btn-ghost"
                                onClick={handleCancel}
                                disabled={saving}
                            >
                                Annuler
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </Layout>
    );
};
